package com.hyperether.goodjob.util

import com.hyperether.goodjob.repository.remote.model.WorkingHours
import com.hyperether.resources.AppLocale
import com.hyperether.resources.currentLanguage
import kotlinx.datetime.Clock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.Month
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atTime
import kotlinx.datetime.daysUntil
import kotlinx.datetime.minus
import kotlinx.datetime.number
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime

object DateTimeUtil {

    fun formatLastAccessed(unixTimestamp: Long): String {
        // Handle possible millisecond timestamp by converting to seconds
        val adjustedTimestamp =
            if (unixTimestamp > 9999999999L) unixTimestamp / 1000 else unixTimestamp

        // Convert the Unix timestamp to a LocalDateTime
        val dateTime = Instant.fromEpochSeconds(adjustedTimestamp)
            .toLocalDateTime(TimeZone.currentSystemDefault())

        // Get the current date and time in the system's time zone
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())

        // Calculate today's and yesterday's date
        val today = now.date
        val yesterday = today.minus(DatePeriod(days = 1))

        // Format the time as HH:mm
        val timeFormatted = "${dateTime.hour.toString().padStart(2, '0')}:${
            dateTime.minute.toString().padStart(2, '0')
        }"

        // Format the date as YYYY-MM-DD
        val dateFormatted = "${dateTime.year}-${
            dateTime.monthNumber.toString().padStart(2, '0')
        }-${dateTime.dayOfMonth.toString().padStart(2, '0')}"

        // Compare and return formatted string
        return when (dateTime.date) {
            today -> {
                if (currentLanguage.value == AppLocale.DE)
                    "Heute, $timeFormatted"
                else
                    "Today, $timeFormatted"
            }
            yesterday -> {
                if (currentLanguage.value == AppLocale.DE)
                    "Gestern, $timeFormatted"
                else
                    "Yesterday, $timeFormatted"
            }
            else -> "$dateFormatted, $timeFormatted"
        }
    }


    fun jobListDateFormatter(dateString: String): String {
        try {
            if (dateString.isEmpty())
                return dateString
            val parsedString = dateString.replace("[", "").replace("]", "")
            val date = parseDate(parsedString)
            val dayOfWeek = if (currentLanguage.value == AppLocale.DE){
                date?.dayOfWeek?.let {
                    getGermanDayName(it)
                } ?: {
                    date?.dayOfWeek?.name?.lowercase()?.replaceFirstChar { it.uppercase() }
                }
            } else {
                date?.dayOfWeek?.name?.lowercase()?.replaceFirstChar { it.uppercase() }
            }
            val day = date?.dayOfMonth.toString().padStart(2, '0')
            val month = if (currentLanguage.value == AppLocale.DE) {
                date?.month?.let {
                    getGermanMonthName(it.number)
                } ?: {
                    date?.month?.name?.lowercase()?.replaceFirstChar { it.uppercase() }
                }
            } else {
                date?.month?.name?.lowercase()?.replaceFirstChar { it.uppercase() }
            }
            val year = date?.year

            return "$dayOfWeek, $day $month $year"
        } catch (e: Exception) {
            println("Cant parse date: $dateString")
            return dateString
        }

    }

    fun formatDateRange(input: String): String? {
        val monthAbbreviations = if (currentLanguage.value == AppLocale.DE) {
            mapOf(
                "Jan" to "01", "Feb" to "02", "Mär" to "03", "Apr" to "04",
                "Mai" to "05", "Jun" to "06", "Jul" to "07", "Aug" to "08",
                "Sep" to "09", "Okt" to "10", "Nov" to "11", "Dez" to "12"
            )
        } else {
            mapOf(
                "Jan" to "01", "Feb" to "02", "Mar" to "03", "Apr" to "04",
                "May" to "05", "Jun" to "06", "Jul" to "07", "Aug" to "08",
                "Sep" to "09", "Oct" to "10", "Nov" to "11", "Dec" to "12"
            )
        }

        if (input.isBlank() || input == "Upcoming leaves") {
            return "Upcoming leaves"
        }

        try {
            val cleanInput = input.removePrefix("upcomingLeave=")

            val parts = cleanInput.split(" - ").map { it.trim() }
            if (parts.size != 2) {
                return null
            }

            val startPart = parts[0]
            val endPart = parts[1]
            println("Start Part: $startPart, End Part: $endPart")

            val startSplit = startPart.split(" ")
            if (startSplit.size != 2) {
                return null
            }

            val monthAbbr = startSplit[0]
            val startDay = startSplit[1].toIntOrNull() ?: return null

            val endSplit = endPart.split(",")
            if (endSplit.size != 2) {
                return null
            }

            val endDay = endSplit[0].toIntOrNull() ?: return null
            val year = endSplit[1].trim().toIntOrNull() ?: 2025
            val monthNumber = monthAbbreviations[monthAbbr] ?: return null

            val startDate = LocalDate(year, monthNumber.toInt(), startDay)
            val endDate = LocalDate(year, monthNumber.toInt(), endDay)

            val formatter = { date: LocalDate ->
                val day = date.dayOfMonth
                val month = date.month.name.take(3).lowercase().replaceFirstChar { it.uppercase() }
                "$day $month"
            }

            return "${formatter(startDate)} - ${formatter(endDate)}, $year"
        } catch (e: Exception) {
            return null
        }
    }

    fun formatJobDetailsDate(inputDate: String): String {
        return try {
            val cleanedDate = inputDate.trim().removeSurrounding("[", "]")

            val instant = if (cleanedDate.contains(" ")) {
                Instant.parse(cleanedDate.replace(" ", "T"))
            } else {
                Instant.parse("${cleanedDate}T00:00:00Z")
            }

            val localDateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())

            val day = localDateTime.dayOfMonth.toString().padStart(2, '0')
            val month = if (currentLanguage.value == AppLocale.DE) {
                getGermanMonthName(localDateTime.month.number)
            } else {
                localDateTime.month.name.lowercase().replaceFirstChar { it.uppercase() }
            }
            val year = localDateTime.year

            "$day $month $year"
        } catch (e: Exception) {
            ""
        }
    }


    private fun parseDate(input: String): LocalDateTime? {
        return try {
            when {
                input.matches(Regex("\\d{4}-\\d{2}-\\d{2}")) -> {
                    // Handle ISO 8601 date (e.g., "2024-12-29")
                    LocalDate.parse(input).atTime(0, 0) // Default to midnight
                }

                input.matches(Regex("\\d+")) -> {
                    // Handle Unix timestamp (e.g., "1703827200")
                    val epochSeconds = input.toLong()
                    Instant.fromEpochSeconds(epochSeconds)
                        .toLocalDateTime(TimeZone.currentSystemDefault())
                }

                else -> null // Unsupported format
            }
        } catch (e: Exception) {
            null // Return null for invalid input
        }
    }

    fun extractDate(unixTimestamp: Long): LocalDate {
        // Handle possible millisecond timestamp by converting to seconds
        val adjustedTimestamp =
            if (unixTimestamp > 9999999999L) unixTimestamp / 1000 else unixTimestamp

        // Convert to LocalDateTime and then extract the date
        return Instant.fromEpochSeconds(adjustedTimestamp)
            .toLocalDateTime(TimeZone.currentSystemDefault())
            .date
    }

    fun formatDateForDisplay(date: LocalDate): String {
        val day = date.dayOfMonth.toString().padStart(2, '0')
        val month = if (currentLanguage.value == AppLocale.DE) {
            getGermanMonthName(date.month.number)
        } else {
            date.month.name.lowercase().replaceFirstChar { it.uppercase() }
        }
        val year = date.year
        val dayOfWeek = if (currentLanguage.value == AppLocale.DE) {
            getGermanDayName(date.dayOfWeek)
        } else {
            date.dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }
        }
        return "$dayOfWeek, $day $month $year"
    }

    fun groupContiguousWorkingHours(workingHours: WorkingHours?): List<Pair<String, String?>> {
        if (workingHours == null) return emptyList()

        // Map days to their schedules
        val dayScheduleMap = if (currentLanguage.value == AppLocale.DE) {
            listOf(
                "Montag" to workingHours.monday,
                "Dienstag" to workingHours.tuesday,
                "Mittwoch" to workingHours.wednesday,
                "Donnerstag" to workingHours.thursday,
                "Freitag" to workingHours.friday,
                "Samstag" to workingHours.saturday,
                "Sonntag" to workingHours.sunday
            )
        } else {
            listOf(
                "Monday" to workingHours.monday,
                "Tuesday" to workingHours.tuesday,
                "Wednesday" to workingHours.wednesday,
                "Thursday" to workingHours.thursday,
                "Friday" to workingHours.friday,
                "Saturday" to workingHours.saturday,
                "Sunday" to workingHours.sunday
            )
        }

        // Filter only enabled days with schedules
        val filteredDays = dayScheduleMap.filter { it.second?.enabled == true }

        // Group contiguous days with the same schedule
        val grouped = mutableListOf<Pair<List<String>, String?>>()
        var currentGroup = mutableListOf<String>()
        var currentSchedule: String? = null

        for ((day, hours) in filteredDays) {
            val schedule = hours?.let { "${it.from} - ${it.to}" }
            if (schedule == currentSchedule) {
                currentGroup.add(day)
            } else {
                if (currentGroup.isNotEmpty()) {
                    grouped.add(currentGroup to currentSchedule)
                }
                currentGroup = mutableListOf(day)
                currentSchedule = schedule
            }
        }

        // Add the final group
        if (currentGroup.isNotEmpty()) {
            grouped.add(currentGroup to currentSchedule)
        }

        return grouped.map { (days, schedule) ->
            // Convert days to "Monday - Wednesday" or just "Monday"
            val dayRange = when {
                days.size > 1 -> "${days.first()} - ${days.last()}"
                else -> days.first()
            }
            dayRange to schedule
        }
    }

    fun formatWorkingHoursText(groupedSchedule: List<Pair<String, String?>>): String {
        return groupedSchedule.joinToString("\n") { (dayRange, schedule) ->
            "$dayRange | ${schedule ?: "Closed"}"
        }
    }

    fun formatInstantToCustomFormat(instant: Instant): String {
        val localDateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
        val month = localDateTime.month.name.take(3).lowercase()
            .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
        val day = localDateTime.dayOfMonth.toString().padStart(2, '0')
        val hour = localDateTime.hour.toString().padStart(2, '0')
        val minute = localDateTime.minute.toString().padStart(2, '0')

        return "$month $day, ${localDateTime.year} - $hour:$minute"
    }

    fun getLastDayOfWeek(date: LocalDate): LocalDate {
        val dayOfWeek = date.dayOfWeek
        val daysUntilEndOfWeek =
            7 - dayOfWeek.ordinal  // Sunday is the last day of the week (ordinal = 6)
        return date.plus(daysUntilEndOfWeek, DateTimeUnit.DAY)
    }

    fun getLastDayOfMonth(date: LocalDate?): LocalDate? {
        if (date == null)
            return null
        val year = date.year
        val month = date.month
        val daysInMonth = daysInMonth(year, month)
        return LocalDate(year, month, daysInMonth)
    }

    fun daysInMonth(year: Int, month: Month): Int {
        return when (month) {
            Month.JANUARY, Month.MARCH, Month.MAY, Month.JULY, Month.AUGUST, Month.OCTOBER, Month.DECEMBER -> 31
            Month.APRIL, Month.JUNE, Month.SEPTEMBER, Month.NOVEMBER -> 30
            Month.FEBRUARY -> if (year.isLeapYear()) 29 else 28
            else -> 30
        }
    }

    fun Int.isLeapYear(): Boolean {
        return (this % 4 == 0 && this % 100 != 0) || (this % 400 == 0)
    }

    fun getLocalDateTimeAhead(localDateTime: LocalDateTime, daysAhead: Int): LocalDateTime {
        val timeZone = TimeZone.currentSystemDefault()
        val instant = localDateTime.toInstant(timeZone)
        val instantDaysLater = instant.plus(daysAhead, DateTimeUnit.DAY, timeZone)
        return instantDaysLater.toLocalDateTime(timeZone)
    }

    fun getLocalDateTimeMonthsAhead(localDateTime: LocalDateTime, monthsAhead: Int): LocalDateTime {
        val timeZone = TimeZone.currentSystemDefault()
        val instant = localDateTime.toInstant(timeZone)
        val instantDaysLater = instant.plus(monthsAhead, DateTimeUnit.MONTH, timeZone)
        return instantDaysLater.toLocalDateTime(timeZone)
    }

    fun formatConsecutiveDates(dates: List<LocalDate>): String {
        if (dates.isEmpty()) return ""

        val sortedDates = dates.sorted()
        val grouped = mutableListOf<List<LocalDate>>()
        var tempList = mutableListOf(sortedDates.first())

        for (i in 1 until sortedDates.size) {
            val prev = sortedDates[i - 1]
            val current = sortedDates[i]

            if (prev.plus(1, DateTimeUnit.DAY) == current) {
                tempList.add(current)
            } else {
                grouped.add(tempList)
                tempList = mutableListOf(current)
            }
        }
        grouped.add(tempList)

        return grouped.joinToString(", ") { group ->
            when (group.size) {
                1 -> group.first().dayOfMonth.toString() + " " + group.first().month
                2 -> "${group.first().dayOfMonth.toString() + " " + group.first().month}, ${group.last().dayOfMonth.toString() + " " + group.last().month}"
                else -> "${group.first().dayOfMonth.toString() + " " + group.first().month} - ${
                    group.last().dayOfMonth.toString() + " " + group.last().month
                }"
            }
        }
    }

    fun formatSelectedDays(dates: List<LocalDate>): String {
        if (dates.isNotEmpty()) {
            val sortedDates = dates.sorted()
            val start = sortedDates.first()
            val end = sortedDates.last()

            fun formatDate(date: LocalDate): String {
                val monthAbbreviation = date.month.name.lowercase().take(3)
                    .replaceFirstChar { it.uppercase() }
                return "$monthAbbreviation ${date.dayOfMonth}"
            }

            val formattedRange =
                if (start.month == end.month && start.year == end.year) {
                    // If the month and year are the same
                    "${formatDate(start)} - ${end.dayOfMonth}, ${end.year}"
                } else {
                    // Full range including months
                    "${formatDate(start)} - ${formatDate(end)}, ${end.year}"
                }
            return formattedRange
        }
        return ""
    }

    fun getLastWorkingDayOfWeek(today: LocalDate): LocalDate {
        val endOfWeek = today.plus(
            (DayOfWeek.FRIDAY.ordinal - today.dayOfWeek.ordinal).toLong(),
            DateTimeUnit.DAY
        )
        return endOfWeek
    }

    fun isWorkingDay(date: LocalDate): Boolean {
        return date.dayOfWeek in DayOfWeek.MONDAY..DayOfWeek.FRIDAY
    }

    fun getFormattedWeekString(start: LocalDate, end: LocalDate): String {
        val startMonth = if (currentLanguage.value == AppLocale.DE) {
            getGermanMonthName(start.month.number).take(3)
        } else {
            start.month.name.take(3).lowercase()
                .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
        }

        val endMonth = if (currentLanguage.value == AppLocale.DE) {
            getGermanMonthName(end.month.number).take(3)
        } else {
            end.month.name.take(3).lowercase()
                .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
        }

        val startDay = start.dayOfMonth
        val endDay = end.dayOfMonth
        val startYear = start.year
        val endYear = end.year

        return if (startMonth == endMonth && startYear == endYear) {
            // Same month and year: "1–8 Sep, 2024" / "1–8 Sep, 2024"
            "$startDay–$endDay $startMonth, $startYear"
        } else if (startYear == endYear) {
            // Same year, different months: "10 Sep - 11 Oct 2024" / "10 Sep - 11 Okt 2024"
            "$startDay $startMonth - $endDay $endMonth $startYear"
        } else {
            // Different years: "30 Dec 2024 - 1 Jan 2025" / "30 Dez 2024 - 1 Jan 2025"
            "$startDay $startMonth $startYear - $endDay $endMonth $endYear"
        }
    }

    fun getNextWeekday(
        dateTime: LocalDateTime,
        targetDayOfWeek: DayOfWeek,
        offsetDays: Int = 7
    ): LocalDateTime {
        var newDate = dateTime.date.plus(offsetDays, DateTimeUnit.DAY)
        while (newDate.dayOfWeek != targetDayOfWeek) {
            newDate = newDate.plus(1, DateTimeUnit.DAY)
        }

        return LocalDateTime(newDate, dateTime.time)
    }

    fun parseDurationToDays(duration: DatePeriod, referenceDate: LocalDate): Int {
        val resultDate = referenceDate.plus(duration)
        return referenceDate.daysUntil(resultDate)
    }

    fun parseIsoPeriod(duration: String): DatePeriod {
        return Regex("""P(\d+M)?(\d+D)?""").find(duration)?.let { match ->
            val months = match.groups[1]?.value?.removeSuffix("M")?.toIntOrNull() ?: 0
            val days = match.groups[2]?.value?.removeSuffix("D")?.toIntOrNull() ?: 0
            DatePeriod(months = months, days = days)
        } ?: DatePeriod()
    }

    fun getDaysInMonth(year: Int, month: Int): Int {
        return when (month) {
            1, 3, 5, 7, 8, 10, 12 -> 31
            4, 6, 9, 11 -> 30
            2 -> if (isItLeapYear(year)) 29 else 28
            else -> throw IllegalArgumentException("Invalid month: $month")
        }
    }

    private fun isItLeapYear(year: Int): Boolean {
        return (year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)
    }

    private fun getGermanDayName(dayOfWeek: DayOfWeek): String {
        return when (dayOfWeek) {
            DayOfWeek.MONDAY -> "Montag"
            DayOfWeek.TUESDAY -> "Dienstag"
            DayOfWeek.WEDNESDAY -> "Mittwoch"
            DayOfWeek.THURSDAY -> "Donnerstag"
            DayOfWeek.FRIDAY -> "Freitag"
            DayOfWeek.SATURDAY -> "Samstag"
            DayOfWeek.SUNDAY -> "Sonntag"
            else -> ""
        }
    }

    private fun getGermanMonthName(monthNumber: Int): String {
        return when (monthNumber) {
            1 -> "Januar"
            2 -> "Februar"
            3 -> "März"
            4 -> "April"
            5 -> "Mai"
            6 -> "Juni"
            7 -> "Juli"
            8 -> "August"
            9 -> "September"
            10 -> "Oktober"
            11 -> "November"
            12 -> "Dezember"
            else -> ""
        }
    }
}